import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Globe,
  MessageSquare,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Megaphone,
  Video,
  BarChart3,
  Users,
  GraduationCap,
  ClipboardList,
  Trophy,
  Upload,
  Activity,
  CheckSquare,
  Calendar,
  BookOpen,
  Facebook,
  ClipboardCheck,
  ChevronDown,
  ChevronRight,
  Building2,
  TrendingUp,
  DollarSign,
  Inbox,
  Lightbulb,
} from "lucide-react";
import { useState, useMemo, useCallback } from "react";
import NotificationBell from "../NotificationBell";
import ChatBubble from "../ChatBubble/ChatBubble";
import SubmitTipModal from "../admin/SubmitTipModal";

// Menu categories for organized navigation
const MENU_CATEGORIES = {
  main: { label: "Main", color: "text-[#CCFF00]" },
  work: { label: "Work", color: "text-blue-400" },
  crm: { label: "CRM", color: "text-purple-400" },
  content: { label: "Content", color: "text-amber-400" },
  system: { label: "System", color: "text-slate-500" },
};

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [tipModalOpen, setTipModalOpen] = useState(false);
  
  const currentUser = useMemo(() => {
    const user = localStorage.getItem("admin_user");
    return user ? JSON.parse(user) : null;
  }, []);

  const isSuperAdmin = currentUser?.role === "super_admin";
  const isVisaStaff = currentUser?.role === "visa_staff";
  const isRecruitmentStaff = currentUser?.role === "recruitment_staff";
  const isHybrid = currentUser?.role === "hybrid";

  const toggleCategory = useCallback((category) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  }, []);

  // Build nav items with categories based on role
  const navItems = useMemo(() => {
    const items = [];

    items.push({ 
      category: "main",
      name: "Dashboard", 
      path: "/tothemoon", 
      icon: LayoutDashboard 
    });

    if (isSuperAdmin) {
      items.push({ category: "work", name: "Tasks", path: "/tothemoon/tasks", icon: CheckSquare });
      items.push({ category: "work", name: "Daily Reports", path: "/tothemoon/daily-reports", icon: ClipboardCheck });
      items.push({ category: "work", name: "Productivity", path: "/tothemoon/productivity", icon: TrendingUp });
      items.push({ category: "work", name: "Leaderboard", path: "/tothemoon/leaderboard", icon: Trophy });
      items.push({ category: "crm", name: "Leads", path: "/tothemoon/inquiries", icon: MessageSquare });
      items.push({ category: "crm", name: "Clients", path: "/tothemoon/clients", icon: GraduationCap });
      items.push({ category: "crm", name: "Office Visits", path: "/tothemoon/office-visits", icon: Building2 });
      items.push({ category: "crm", name: "Checklists", path: "/tothemoon/checklists", icon: ClipboardList });
      items.push({ category: "crm", name: "Finance", path: "/tothemoon/finance", icon: DollarSign });
      items.push({ category: "crm", name: "Bulk Upload", path: "/tothemoon/bulk-upload", icon: Upload });
      items.push({ category: "crm", name: "FB Import", path: "/tothemoon/facebook-import", icon: Facebook });
      items.push({ category: "content", name: "Courses", path: "/tothemoon/courses", icon: BookOpen });
      items.push({ category: "content", name: "Destinations", path: "/tothemoon/destinations", icon: Globe });
      items.push({ category: "content", name: "Blog", path: "/tothemoon/blog", icon: FileText });
      items.push({ category: "content", name: "Promotions", path: "/tothemoon/promotions", icon: Calendar });
      items.push({ category: "content", name: "Promo Tabs", path: "/tothemoon/promotional-tabs", icon: Megaphone });
      items.push({ category: "content", name: "Videos", path: "/tothemoon/video-testimonials", icon: Video });
      items.push({ category: "content", name: "Knowledge", path: "/tothemoon/knowledge-base", icon: BookOpen });
      items.push({ category: "system", name: "Users", path: "/tothemoon/users", icon: Users });
      items.push({ category: "system", name: "Tips Inbox", path: "/tothemoon/staff-tips", icon: Inbox });
      items.push({ category: "system", name: "Partners", path: "/tothemoon/partners", icon: Users });
      items.push({ category: "system", name: "Activity", path: "/tothemoon/activity", icon: Activity });
      items.push({ category: "system", name: "Analytics", path: "/tothemoon/analytics", icon: BarChart3 });
      items.push({ category: "system", name: "Settings", path: "/tothemoon/settings", icon: Settings });
    }
    else if (isHybrid) {
      items.push({ category: "work", name: "Tasks", path: "/tothemoon/tasks", icon: CheckSquare });
      items.push({ category: "work", name: "Daily Reports", path: "/tothemoon/daily-reports", icon: ClipboardCheck });
      items.push({ category: "work", name: "Productivity", path: "/tothemoon/productivity", icon: TrendingUp });
      items.push({ category: "work", name: "Leaderboard", path: "/tothemoon/leaderboard", icon: Trophy });
      items.push({ category: "crm", name: "Leads", path: "/tothemoon/inquiries", icon: MessageSquare });
      items.push({ category: "crm", name: "Clients", path: "/tothemoon/clients", icon: GraduationCap });
      items.push({ category: "crm", name: "Office Visits", path: "/tothemoon/office-visits", icon: Building2 });
      items.push({ category: "crm", name: "Checklists", path: "/tothemoon/checklists", icon: ClipboardList });
      items.push({ category: "crm", name: "Finance", path: "/tothemoon/finance", icon: DollarSign });
      items.push({ category: "crm", name: "Bulk Upload", path: "/tothemoon/bulk-upload", icon: Upload });
      items.push({ category: "crm", name: "FB Import", path: "/tothemoon/facebook-import", icon: Facebook });
      items.push({ category: "content", name: "Courses", path: "/tothemoon/courses", icon: BookOpen });
      items.push({ category: "content", name: "Promotions", path: "/tothemoon/promotions", icon: Calendar });
      items.push({ category: "content", name: "Knowledge", path: "/tothemoon/knowledge-base", icon: BookOpen });
      items.push({ category: "system", name: "Tips Inbox", path: "/tothemoon/staff-tips", icon: Inbox });
      items.push({ category: "system", name: "Partners", path: "/tothemoon/partners", icon: Users });
      items.push({ category: "system", name: "Activity", path: "/tothemoon/activity", icon: Activity });
    }
    else if (isRecruitmentStaff) {
      items.push({ category: "work", name: "Tasks", path: "/tothemoon/tasks", icon: CheckSquare });
      items.push({ category: "work", name: "Daily Reports", path: "/tothemoon/daily-reports", icon: ClipboardCheck });
      items.push({ category: "work", name: "Leaderboard", path: "/tothemoon/leaderboard", icon: Trophy });
      items.push({ category: "crm", name: "My Leads", path: "/tothemoon/inquiries", icon: MessageSquare });
      items.push({ category: "crm", name: "Clients", path: "/tothemoon/clients", icon: GraduationCap });
      items.push({ category: "crm", name: "Office Visits", path: "/tothemoon/office-visits", icon: Building2 });
      items.push({ category: "content", name: "Courses", path: "/tothemoon/courses", icon: BookOpen });
      items.push({ category: "content", name: "Promotions", path: "/tothemoon/promotions", icon: Calendar });
      items.push({ category: "content", name: "Knowledge", path: "/tothemoon/knowledge-base", icon: BookOpen });
    }
    else if (isVisaStaff) {
      items.push({ category: "work", name: "Tasks", path: "/tothemoon/tasks", icon: CheckSquare });
      items.push({ category: "work", name: "Daily Reports", path: "/tothemoon/daily-reports", icon: ClipboardCheck });
      items.push({ category: "crm", name: "My Clients", path: "/tothemoon/clients", icon: GraduationCap });
      items.push({ category: "crm", name: "Office Visits", path: "/tothemoon/office-visits", icon: Building2 });
      items.push({ category: "crm", name: "Checklists", path: "/tothemoon/checklists", icon: ClipboardList });
      items.push({ category: "content", name: "Courses", path: "/tothemoon/courses", icon: BookOpen });
      items.push({ category: "content", name: "Promotions", path: "/tothemoon/promotions", icon: Calendar });
      items.push({ category: "content", name: "Knowledge", path: "/tothemoon/knowledge-base", icon: BookOpen });
    }
    else {
      items.push({ category: "work", name: "Tasks", path: "/tothemoon/tasks", icon: CheckSquare });
      items.push({ category: "crm", name: "Inquiries", path: "/tothemoon/inquiries", icon: MessageSquare });
      items.push({ category: "crm", name: "Clients", path: "/tothemoon/clients", icon: GraduationCap });
      items.push({ category: "content", name: "Destinations", path: "/tothemoon/destinations", icon: Globe });
      items.push({ category: "content", name: "Blog", path: "/tothemoon/blog", icon: FileText });
      items.push({ category: "system", name: "Analytics", path: "/tothemoon/analytics", icon: BarChart3 });
      items.push({ category: "system", name: "Settings", path: "/tothemoon/settings", icon: Settings });
    }

    return items;
  }, [isSuperAdmin, isHybrid, isRecruitmentStaff, isVisaStaff]);
  
  // Group items by category
  const groupedItems = useMemo(() => {
    return navItems.reduce((acc, item) => {
      if (!acc[item.category]) acc[item.category] = [];
      acc[item.category].push(item);
      return acc;
    }, {});
  }, [navItems]);

  const handleLogout = useCallback(async () => {
    try {
      // Call logout API to clear httpOnly cookie
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.warn('Logout API error:', error);
    } finally {
      // Always clear localStorage regardless of API result
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_user");
      navigate("/tothemoon/login");
    }
  }, [navigate]);

  const isActive = useCallback((path) => {
    if (path === "/tothemoon") {
      return location.pathname === "/tothemoon";
    }
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  const closeMobileSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  // Render sidebar navigation
  const renderNavigation = (mobile = false) => (
    <nav className="flex-1 overflow-y-auto py-2 px-2">
      {Object.entries(groupedItems).map(([category, items]) => {
        const config = MENU_CATEGORIES[category];
        const isCollapsed = collapsedCategories[category];
        
        return (
          <div key={category} className="mb-1">
            {category !== "main" && (
              <button
                onClick={() => toggleCategory(category)}
                className="w-full flex items-center justify-between px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600 hover:text-slate-400 transition-colors"
              >
                <span className={config.color}>{config.label}</span>
                {isCollapsed ? (
                  <ChevronRight className="w-3 h-3" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}
              </button>
            )}
            
            {!isCollapsed && (
              <div className="space-y-0.5">
                {items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={mobile ? closeMobileSidebar : undefined}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all ${
                      isActive(item.path)
                        ? "bg-[#CCFF00]/10 text-[#CCFF00] border-l-2 border-[#CCFF00]"
                        : "text-slate-400 hover:bg-[#1a1a1a] hover:text-white"
                    }`}
                    data-testid={`admin-nav-${item.name.toLowerCase().replace(" ", "-")}`}
                  >
                    <item.icon className={`w-4 h-4 flex-shrink-0 ${isActive(item.path) ? "text-[#CCFF00]" : ""}`} />
                    <span className="truncate">{item.name}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-[#0d0d0d]">
      {/* Sidebar - Desktop */}
      <aside 
        className="hidden lg:flex flex-col w-52 bg-[#0d0d0d] text-white fixed h-screen border-r border-[#222]"
        data-testid="admin-sidebar"
      >
        {/* Logo Header */}
        <div className="p-4 border-b border-[#222]">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="Sprungly"
              className="h-8 w-auto"
            />
          </Link>
        </div>

        {/* User Info - Compact */}
        <div className="px-3 py-3 border-b border-[#222]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#CCFF00] to-[#9ACD00] flex items-center justify-center text-black font-bold text-sm">
              {currentUser?.name?.charAt(0) || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{currentUser?.name || "User"}</p>
              <p className="text-[10px] text-slate-500 truncate capitalize">{currentUser?.role?.replace("_", " ") || "Staff"}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        {renderNavigation(false)}

        {/* Footer */}
        <div className="p-2 border-t border-[#222] space-y-1">
          <button
            onClick={() => setTipModalOpen(true)}
            className="flex items-center gap-2.5 px-3 py-2 w-full rounded-lg bg-[#CCFF00]/10 text-[#CCFF00] hover:bg-[#CCFF00]/20 transition-all text-sm font-medium"
            data-testid="open-submit-tip"
          >
            <Lightbulb className="w-4 h-4" />
            <span>Share an idea</span>
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2.5 px-3 py-2 w-full rounded-lg text-slate-500 hover:bg-red-500/10 hover:text-red-400 transition-all text-sm"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Menu Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-[#0d0d0d] text-[#CCFF00] rounded-lg shadow-lg border border-[#222]"
        data-testid="mobile-menu-btn"
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/80 z-40 backdrop-blur-sm"
          onClick={closeMobileSidebar}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`lg:hidden fixed top-0 left-0 h-full w-64 bg-[#0d0d0d] text-white z-50 transform transition-transform duration-300 border-r border-[#222] ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo Header */}
        <div className="p-4 border-b border-[#222]">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="Sprungly"
              className="h-8 w-auto"
            />
          </Link>
        </div>

        {/* User Info */}
        <div className="px-3 py-3 border-b border-[#222]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#CCFF00] to-[#9ACD00] flex items-center justify-center text-black font-bold text-sm">
              {currentUser?.name?.charAt(0) || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{currentUser?.name || "User"}</p>
              <p className="text-[10px] text-slate-500 truncate capitalize">{currentUser?.role?.replace("_", " ") || "Staff"}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        {renderNavigation(true)}

        {/* Footer */}
        <div className="p-2 border-t border-[#222]">
          <button
            onClick={handleLogout}
            className="flex items-center gap-2.5 px-3 py-2 w-full rounded-lg text-slate-500 hover:bg-red-500/10 hover:text-red-400 transition-all text-sm"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-52 flex flex-col min-h-screen">
        {/* Top Header Bar */}
        <header className="sticky top-0 z-30 bg-[#111] border-b border-[#222]">
          <div className="flex items-center justify-between px-4 lg:px-6 py-3">
            <div className="flex items-center gap-3 lg:hidden">
              <div className="w-8" />
            </div>
            
            <div className="flex-1" />
            
            <div className="flex items-center gap-3">
              <NotificationBell />
              
              <Link
                to="/"
                target="_blank"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-[#CCFF00] hover:bg-[#CCFF00]/5 rounded-lg transition-colors"
              >
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">View Site</span>
              </Link>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6 bg-[#0d0d0d]" data-testid="admin-content">
          <Outlet />
        </main>
      </div>

      {/* Team Chat Bubble */}
      <ChatBubble />

      {/* Staff Tip submission modal — accessible to all roles */}
      <SubmitTipModal open={tipModalOpen} onClose={() => setTipModalOpen(false)} />
    </div>
  );
};

export default AdminLayout;
